/**
 * Observed frequency of each number over a window of draws.
 *
 * This is DESCRIPTIVE, and it is the only thing it is. A number that came out more
 * often than average over the last six months is not more likely to come out next
 * time, and a number that came out less often is not "due" — the draws are
 * independent, which the chi-squared test on the same window is there to show.
 *
 * Nothing in this module is read by the generator. `draw.ts` weights numbers by the
 * popularity model — how often people PLAY them — and never by how often they have
 * been DRAWN. Wiring these counts into selection is the one change this project
 * forbids outright.
 */

export interface NumberFrequency {
  /** The number itself, 1..nmax. */
  number: number;
  /** How many times it was drawn in the window. */
  count: number;
  /** How many times a uniform law would have drawn it, on average. */
  expected: number;
  /** Standardised deviation from `expected`, in units of its standard deviation. */
  z: number;
  /** count relative to the highest count in the window, in [0, 1]. For bar widths. */
  share: number;
}

/**
 * Counts per number, ordered most-drawn first. Ties are broken by the number
 * itself, so the ordering is stable and reproducible.
 *
 * @param picks   one array of numbers per draw
 * @param nmax    numbers run from 1 to nmax
 * @param perDraw how many numbers each draw contributes
 */
export function frequencyTable(
  picks: readonly (readonly number[])[],
  nmax: number,
  perDraw: number,
): NumberFrequency[] {
  const counts = new Array<number>(nmax).fill(0);
  for (const draw of picks) {
    for (const x of draw) {
      if (x >= 1 && x <= nmax) counts[x - 1] = (counts[x - 1] as number) + 1;
    }
  }

  const total = picks.length * perDraw;
  const p = 1 / nmax;
  const expected = total * p;
  // Binomial standard deviation of a count, not sqrt(expected): the total number of
  // balls drawn is fixed, so the Poisson approximation would overstate the spread.
  const sd = Math.sqrt(total * p * (1 - p));

  let max = 0;
  for (const c of counts) if (c > max) max = c;

  const rows: NumberFrequency[] = counts.map((count, i) => ({
    number: i + 1,
    count,
    expected,
    z: sd > 0 ? (count - expected) / sd : 0,
    share: max > 0 ? count / max : 0,
  }));

  rows.sort((a, b) => (b.count !== a.count ? b.count - a.count : a.number - b.number));
  return rows;
}
